import React, { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { getFirebaseFunctions } from "@repo/backend/firebase";
import { BASE_PLAN, ADD_ONS, type BillingInterval } from "@repo/backend/billing/tiers";
import type { SectionComponentProps } from "../../registry.js";

interface PricingTiersProps {
  headline?: string;
  subheadline?: string;
}

export function PricingTiers({ props, clientId }: SectionComponentProps) {
  const {
    headline = "One flat price. Everything included.",
    subheadline = "No setup fees. No contracts. Add capabilities à la carte after you're live.",
  } = props as PricingTiersProps;

  const [interval, setInterval] = useState<BillingInterval>("monthly");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const displayPrice = interval === "yearly" ? BASE_PLAN.yearlyPrice : BASE_PLAN.monthlyPrice;
  const displayUnit = interval === "yearly" ? "yr" : "mo";

  async function handleGetStarted() {
    setLoading(true);
    setError(null);
    try {
      const fns = getFirebaseFunctions();
      const createCheckout = httpsCallable<
        { clientId: string; billingInterval: BillingInterval; successUrl: string; cancelUrl: string },
        { url: string }
      >(fns, "createSubscriptionCheckout");

      const result = await createCheckout({
        clientId,
        billingInterval: interval,
        successUrl: `${window.location.origin}/subscription/success`,
        cancelUrl: window.location.href,
      });
      if (result.data.url) {
        window.location.href = result.data.url;
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
      console.error("[PricingTiers] checkout error:", err);
      setLoading(false);
    }
  }

  const revenueAddOns = ADD_ONS.filter((a) => a.category === "revenue");
  const automationAddOns = ADD_ONS.filter((a) => a.category === "automation");

  return (
    <section className="py-20 px-6 bg-background" id="pricing">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10 space-y-3">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">{headline}</h2>
          {subheadline && (
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">{subheadline}</p>
          )}
        </div>

        {/* Billing interval toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex rounded-full border border-border bg-card p-1 gap-1">
            <button
              onClick={() => setInterval("monthly")}
              className={[
                "px-5 py-1.5 rounded-full text-sm font-medium transition-colors",
                interval === "monthly"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              Monthly
            </button>
            <button
              onClick={() => setInterval("yearly")}
              className={[
                "px-5 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-2",
                interval === "yearly"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              Yearly
              <span
                className={[
                  "text-xs font-semibold px-1.5 py-0.5 rounded-full",
                  interval === "yearly"
                    ? "bg-primary-foreground/20 text-primary-foreground"
                    : "bg-primary/10 text-primary",
                ].join(" ")}
              >
                2 months free
              </span>
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-8 text-center text-sm text-destructive">{error}</div>
        )}

        {/* Base plan card */}
        <div className="rounded-[var(--radius)] border border-primary bg-primary text-primary-foreground p-8 mb-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex-1 space-y-2">
            <div className="text-xs font-semibold uppercase tracking-widest opacity-75">Base Plan</div>
            <div className="text-2xl font-bold">Your site, hosted and maintained.</div>
            <p className="opacity-80 text-sm max-w-md">{BASE_PLAN.tagline}</p>
            <ul className="mt-4 space-y-1.5">
              {BASE_PLAN.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm opacity-90">
                  <svg className="mt-0.5 shrink-0 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col items-center md:items-end gap-4 shrink-0">
            <div className="text-center md:text-right">
              <div className="flex items-end gap-1 justify-center md:justify-end">
                <span className="text-5xl font-bold">${displayPrice}</span>
                <span className="text-sm opacity-75 mb-1.5">/ {displayUnit}</span>
              </div>
              {interval === "yearly" ? (
                <div className="text-xs opacity-70">${Math.round(BASE_PLAN.yearlyPrice / 12)}/mo — save ${BASE_PLAN.monthlyPrice * 2}</div>
              ) : (
                <div className="text-xs opacity-70">or ${BASE_PLAN.yearlyPrice}/yr — save ${BASE_PLAN.monthlyPrice * 2}</div>
              )}
            </div>
            <button
              onClick={handleGetStarted}
              disabled={loading}
              className="w-full md:w-auto bg-background text-foreground px-8 py-3 rounded-[var(--radius)] text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loading ? "Redirecting…" : "Get started →"}
            </button>
          </div>
        </div>

        {/* Add-ons */}
        <div className="space-y-8">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-foreground">Add-ons — unlock after purchase</h3>
            <p className="text-muted-foreground text-sm mt-1">Bolt on exactly what your business needs. Cancel individual add-ons anytime.</p>
          </div>

          <AddOnGroup
            title="Revenue"
            addOns={revenueAddOns}
            interval={interval}
          />
          <AddOnGroup
            title="Automation"
            addOns={automationAddOns}
            interval={interval}
          />
        </div>
      </div>
    </section>
  );
}

function AddOnGroup({
  title,
  addOns,
  interval,
}: {
  title: string;
  addOns: typeof ADD_ONS;
  interval: BillingInterval;
}) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {addOns.map((addon) => {
          const price = interval === "yearly" ? addon.yearlyPrice : addon.monthlyPrice;
          const unit = interval === "yearly" ? "yr" : "mo";
          return (
            <div
              key={addon.id}
              className="rounded-[var(--radius)] border border-border bg-card p-5 flex flex-col gap-3"
            >
              <div>
                <div className="font-semibold text-card-foreground text-sm">{addon.label}</div>
                <div className="text-muted-foreground text-xs mt-1">{addon.description}</div>
              </div>
              <div className="mt-auto flex items-end gap-1">
                <span className="text-2xl font-bold text-card-foreground">+${price}</span>
                <span className="text-xs text-muted-foreground mb-0.5">/ {unit}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
